import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    setLoading(true);
    const result = await login(username, password);
    setLoading(false);
    
    if (result.success) {
      toast.success("Connexion réussie");
      navigate("/dashboard");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Logo */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative items-center justify-center">
        <div className="text-center p-12">
          <div className="bg-white rounded-2xl p-8 shadow-2xl inline-block">
            <img 
              src="/logo.png" 
              alt="Carrosserie Auto Gardoise" 
              className="w-80 h-auto object-contain" 
            />
          </div>
          <p className="text-xl text-slate-300 mt-8 max-w-md mx-auto">
            Gérez votre atelier, vos rendez-vous et le suivi de vos réparations.
          </p>
        </div>
      </div>
      
      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="space-y-2 pb-6">
            <div className="lg:hidden mb-4">
              <img 
                src="/logo.png" 
                alt="Carrosserie Auto Gardoise" 
                className="w-48 h-auto mx-auto object-contain" 
              />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">Connexion</CardTitle>
            <CardDescription className="text-slate-600">
              Entrez vos identifiants pour accéder à votre espace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-semibold text-slate-700">
                  Identifiant
                </Label>
                <Input
                  id="username"
                  data-testid="login-username"
                  type="text"
                  placeholder="Votre identifiant"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                  Mot de passe
                </Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <LogIn className="mr-2 h-5 w-5" />
                    Se connecter
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
